import type {
  DomainSelectDto,
  EffectSelectDto,
  MissionAssessmentEffectAiGenerationDataDto,
  MissionAssessmentEffectCostDto,
  MissionAssessmentEffectCreateDto,
  MissionAssessmentEffectTimeDto,
  TargetSystemSelectDto,
  UseSelectDto,
} from '@@types/api.types';
import { MissionAssessmentEffectCostDtoCurrencyEnum, MissionAssessmentEffectTimeUnit } from '@@types/api.types';
import { asOptionalNumber, asOptionalRecord, asOptionalString, asStringArray, isEnumValue } from '@utils/parse.util';

type EffectSuggestionCatalogs = {
  domains: DomainSelectDto[];
  effects: EffectSelectDto[];
  targetSystems: TargetSystemSelectDto[];
  uses: UseSelectDto[];
};

type EffectSuggestionCatalogKind = 'domain' | 'effect' | 'targetSystem' | 'use';

type EffectSuggestionItem = {
  id: string;
  name: string;
};

const UNKNOWN_LABEL_BY_KIND: Record<EffectSuggestionCatalogKind, string> = {
  domain: 'Domain',
  effect: 'Effect',
  targetSystem: 'Target System',
  use: 'Use',
};

const createUnknownLabel = (kind: EffectSuggestionCatalogKind, id?: string): string => {
  const label = UNKNOWN_LABEL_BY_KIND[kind];

  if (!id) return `Unknown ${label}`;

  return `Unknown ${label} (${id.slice(0, 8)})`;
};

const findDomainName = (params: { domains: DomainSelectDto[]; id: string }): string | undefined => {
  const { domains, id } = params;

  for (const domain of domains) {
    if (domain.id === id) return domain.name;

    const subdomain = domain.subdomains.find((item) => item.id === id);
    if (subdomain) return subdomain.name;
  }

  return undefined;
};

const findEffectName = (params: { effects: EffectSelectDto[]; id: string }): string | undefined => {
  const { effects, id } = params;

  for (const effect of effects) {
    if (effect.id === id) return effect.name;

    const secondaryEffect = effect.secondaryEffects.find((item) => item.id === id);
    if (secondaryEffect) return secondaryEffect.name;
  }

  return undefined;
};

export const getEffectSuggestionCatalogName = (params: {
  catalogs: EffectSuggestionCatalogs;
  id?: string;
  kind: EffectSuggestionCatalogKind;
}): string => {
  const { catalogs, id, kind } = params;

  if (!id) {
    return createUnknownLabel(kind);
  }

  if (kind === 'domain') {
    return findDomainName({ domains: catalogs.domains, id }) ?? createUnknownLabel(kind, id);
  }

  if (kind === 'effect') {
    return findEffectName({ effects: catalogs.effects, id }) ?? createUnknownLabel(kind, id);
  }

  if (kind === 'targetSystem') {
    return catalogs.targetSystems.find((item) => item.id === id)?.name ?? createUnknownLabel(kind, id);
  }

  return catalogs.uses.find((item) => item.id === id)?.name ?? createUnknownLabel(kind, id);
};

export const getEffectSuggestionCatalogItems = (params: {
  catalogs: EffectSuggestionCatalogs;
  ids?: string[];
  kind: EffectSuggestionCatalogKind;
}): EffectSuggestionItem[] => {
  const { catalogs, ids, kind } = params;

  return (ids ?? []).map((id) => ({
    id,
    name: getEffectSuggestionCatalogName({ catalogs, id, kind }),
  }));
};

const requireCatalogId = (params: {
  kind: EffectSuggestionCatalogKind;
  value: string | undefined;
}): string => {
  const { kind, value } = params;

  if (!value) {
    throw new Error(`Generated Mission Assessment Effect is missing the ${UNKNOWN_LABEL_BY_KIND[kind]} ID.`);
  }

  return value;
};

export const normalizeGeneratedMissionAssessmentEffect = (
  data: MissionAssessmentEffectAiGenerationDataDto,
): MissionAssessmentEffectCreateDto => {
  return {
    data: {
      cost: data.data.cost,
      costToReplace: data.data.costToReplace,
      description: data.data.description ?? '',
      maxPhase: data.data.maxPhase,
      measureOfImpact: data.data.measureOfImpact ?? '',
      measuresOfPerformance: data.data.measuresOfPerformance ?? '',
      minPhase: data.data.minPhase,
      personnelRequired: data.data.personnelRequired,
      timeToDeploy: data.data.timeToDeploy,
      timeToReplace: data.data.timeToReplace,
      unit: data.data.unit ?? '',
    },
    domainId: requireCatalogId({ kind: 'domain', value: data.domainId }),
    effectId: requireCatalogId({ kind: 'effect', value: data.effectId }),
    secondaryEffects: data.secondaryEffects ?? [],
    targetSystems: data.targetSystems ?? [],
    useId: requireCatalogId({ kind: 'use', value: data.useId }),
  };
};

// =============================================================================
// CHAT ARTIFACT PARSING
// =============================================================================

const parseGeneratedEffectCost = (value: unknown): MissionAssessmentEffectCostDto | undefined => {
  const record = asOptionalRecord(value);
  const amount = asOptionalNumber(record?.amount);

  if (
    record === undefined ||
    amount === undefined ||
    !isEnumValue(MissionAssessmentEffectCostDtoCurrencyEnum, record.currency)
  ) {
    return undefined;
  }

  return { amount, currency: record.currency };
};

const parseGeneratedEffectTime = (value: unknown): MissionAssessmentEffectTimeDto | undefined => {
  const record = asOptionalRecord(value);
  const timeValue = asOptionalNumber(record?.value);

  if (record === undefined || timeValue === undefined || !isEnumValue(MissionAssessmentEffectTimeUnit, record.unit)) {
    return undefined;
  }

  return { unit: record.unit, value: timeValue };
};

/**
 * Parse a server-resolved chat artifact object into the effect generation shape.
 * Returns undefined when the required catalog references are missing.
 */
export const parseGeneratedMissionAssessmentEffectData = (
  value: Record<string, unknown>,
): MissionAssessmentEffectAiGenerationDataDto | undefined => {
  const data = asOptionalRecord(value.data);
  const domainId = asOptionalString(value.domainId);
  const effectId = asOptionalString(value.effectId);
  const useId = asOptionalString(value.useId);

  if (!data || !domainId || !effectId || !useId) {
    return undefined;
  }

  return {
    data: {
      cost: parseGeneratedEffectCost(data.cost),
      costToReplace: parseGeneratedEffectCost(data.costToReplace),
      description: asOptionalString(data.description),
      maxPhase: asOptionalNumber(data.maxPhase),
      measureOfImpact: asOptionalString(data.measureOfImpact),
      measuresOfPerformance: asOptionalString(data.measuresOfPerformance),
      minPhase: asOptionalNumber(data.minPhase),
      personnelRequired: asOptionalNumber(data.personnelRequired),
      timeToDeploy: parseGeneratedEffectTime(data.timeToDeploy),
      timeToReplace: parseGeneratedEffectTime(data.timeToReplace),
      unit: asOptionalString(data.unit),
    },
    domainId,
    effectId,
    secondaryEffects: asStringArray(value.secondaryEffects) ?? [],
    targetSystems: asStringArray(value.targetSystems) ?? [],
    useId,
  };
};
